import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, Search, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CYLINDER_TYPES = ["Hydraulic", "Pneumatic", "Telescopic", "Servo", "Tie-Rod", "Welded", "Mill-Duty"];

/**
 * LineItemsEditor
 * Props:
 *  value: array of line items
 *  onChange: (newLines) => void
 *  showPrice: boolean — show unit_price column (for quotation/PO/OA)
 *  readOnly: boolean
 *  customerId: string — enables per-customer Material Code + dimension lookups
 */
export default function LineItemsEditor({ value = [], onChange, showPrice = false, readOnly = false, customerId = null }) {
  const items = value;

  function update(i, patch) {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  }

  function add() {
    const next = [...items, {
      description: "", cylinder_type: "Hydraulic", manufacturing_type: "New",
      customer_material_code: "", application: "",
      bore_mm: 100, rod_diameter_mm: 56, stroke_mm: 500, quantity: 1,
      operating_pressure_bar: 210, remarks: "", unit_price: showPrice ? 0 : undefined,
    }];
    onChange(next);
  }

  function remove(i) {
    onChange(items.filter((_, idx) => idx !== i));
  }

  function copyFromMatch(i, m) {
    update(i, {
      customer_material_code: m.customer_material_code || items[i].customer_material_code,
      description: m.description || items[i].description,
      cylinder_type: m.cylinder_type || items[i].cylinder_type,
      manufacturing_type: m.manufacturing_type || items[i].manufacturing_type,
      bore_mm: m.bore_mm ?? items[i].bore_mm,
      rod_diameter_mm: m.rod_diameter_mm ?? items[i].rod_diameter_mm,
      stroke_mm: m.stroke_mm ?? items[i].stroke_mm,
      operating_pressure_bar: m.operating_pressure_bar ?? items[i].operating_pressure_bar,
      unit_price: showPrice && m.unit_price != null ? m.unit_price : items[i].unit_price,
    });
    toast.success(`Copied from ${m.inquiry_number || "previous inquiry"}`);
  }

  const total = items.reduce((sum, it) => sum + (parseFloat(it.unit_price || 0) * parseInt(it.quantity || 0)), 0);

  // READ-ONLY view keeps the compact table (used on detail pages)
  if (readOnly) {
    return (
      <div className="space-y-2">
        <div className="overflow-x-auto border border-slate-200 rounded-md">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-2 py-2 text-left w-8">#</th>
                <th className="px-2 py-2 text-left min-w-[180px]">Description</th>
                <th className="px-2 py-2 text-left min-w-[140px]">Cust. Material Code</th>
                <th className="px-2 py-2 text-left min-w-[120px]">Type</th>
                <th className="px-2 py-2 text-left min-w-[110px]">Workflow</th>
                <th className="px-2 py-2 text-left min-w-[120px]">Application</th>
                <th className="px-2 py-2 text-left w-24">Bore (mm)</th>
                <th className="px-2 py-2 text-left w-24">Rod Ø (mm)</th>
                <th className="px-2 py-2 text-left w-24">Stroke (mm)</th>
                <th className="px-2 py-2 text-left w-28">Pressure (bar)</th>
                <th className="px-2 py-2 text-left w-20">Qty (Nos.)</th>
                {showPrice && <th className="px-2 py-2 text-right w-24">Unit ₹</th>}
                {showPrice && <th className="px-2 py-2 text-right w-24">Total ₹</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 && (
                <tr><td colSpan={showPrice ? 13 : 11} className="text-center py-6 text-xs text-slate-400">No line items.</td></tr>
              )}
              {items.map((it, i) => (
                <tr key={i} data-testid={`line-row-${i}`}>
                  <td className="px-2 py-1.5 font-mono text-xs text-slate-500">{i + 1}</td>
                  <td className="px-2 py-1.5 text-xs">{it.description}</td>
                  <td className="px-2 py-1.5 text-xs font-mono">{it.customer_material_code || "—"}</td>
                  <td className="px-2 py-1.5 text-xs">{it.cylinder_type}</td>
                  <td className="px-2 py-1.5">
                    <span className={`text-[10px] uppercase font-semibold font-mono px-1.5 py-0.5 rounded border ${
                      it.manufacturing_type === "Reconditioning"
                        ? "bg-rose-50 text-rose-700 border-rose-200"
                        : "bg-blue-50 text-blue-800 border-blue-200"}`}>
                      {it.manufacturing_type === "Reconditioning" ? "Recon" : "New"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-xs">{it.application || "—"}</td>
                  <td className="px-2 py-1.5 text-xs font-mono">{it.bore_mm ?? "—"}</td>
                  <td className="px-2 py-1.5 text-xs font-mono">{it.rod_diameter_mm ?? "—"}</td>
                  <td className="px-2 py-1.5 text-xs font-mono">{it.stroke_mm ?? "—"}</td>
                  <td className="px-2 py-1.5 text-xs font-mono">{it.operating_pressure_bar ?? "—"}</td>
                  <td className="px-2 py-1.5 text-xs font-mono">{it.quantity ?? "—"}</td>
                  {showPrice && <td className="px-2 py-1.5 text-xs font-mono text-right">{it.unit_price != null ? `₹${Number(it.unit_price).toLocaleString("en-IN")}` : "—"}</td>}
                  {showPrice && <td className="px-2 py-1.5 text-xs font-mono text-right font-semibold">₹{((it.unit_price || 0) * (it.quantity || 0)).toLocaleString("en-IN")}</td>}
                </tr>
              ))}
            </tbody>
            {showPrice && items.length > 0 && (
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td colSpan={11} className="px-2 py-2 text-right text-xs uppercase tracking-wider font-semibold text-slate-600">Total</td>
                  <td></td>
                  <td className="px-2 py-2 text-right font-mono font-bold text-slate-900" data-testid="line-total">
                    ₹{total.toLocaleString("en-IN")}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  }

  // EDIT view: two-row card per cylinder line — desktop-optimised, no horizontal scroll
  return (
    <div className="space-y-3">
      {items.length === 0 && (
        <div className="text-center py-8 text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg">
          No cylinder line items yet. Click "Add Line" below.
        </div>
      )}

      {items.map((it, i) => (
        <div key={i} data-testid={`line-row-${i}`}
             className="border border-slate-200 rounded-lg bg-white hover:border-slate-300 transition-colors">
          {/* Card header — line number + delete */}
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 bg-slate-50/60 rounded-t-lg">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Cylinder Line {i + 1}
            </div>
            <Button type="button" variant="ghost" size="icon" data-testid={`line-${i}-remove`}
                    className="h-7 w-7 text-red-600 hover:bg-red-50" onClick={() => remove(i)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>

          <div className="p-3 space-y-3">
            {/* ROW 1 — IDENTIFICATION */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-4">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Description</label>
                <Input data-testid={`line-${i}-description`} className="h-9 text-sm mt-1"
                       value={it.description || ""} placeholder="Cylinder for..."
                       onChange={(e) => update(i, { description: e.target.value })} />
              </div>
              <div className="md:col-span-3">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Customer Material Code</label>
                <div className="mt-1">
                  <MaterialCodeCell
                    idx={i} value={it.customer_material_code || ""} customerId={customerId} line={it}
                    onChange={(v) => update(i, { customer_material_code: v })}
                    onCopy={(m) => copyFromMatch(i, m)}
                    readOnly={readOnly} showPrice={showPrice}
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Type</label>
                <Select value={it.cylinder_type || "Hydraulic"} onValueChange={(v) => update(i, { cylinder_type: v })}>
                  <SelectTrigger className="h-9 text-sm mt-1" data-testid={`line-${i}-type`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CYLINDER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Workflow</label>
                <Select value={it.manufacturing_type || "New"} onValueChange={(v) => update(i, { manufacturing_type: v })}>
                  <SelectTrigger className="h-9 text-sm mt-1" data-testid={`line-${i}-mfg-type`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New">New Manufacturing</SelectItem>
                    <SelectItem value="Reconditioning">Reconditioning</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Application</label>
                <Input data-testid={`line-${i}-application`} className="h-9 text-sm mt-1"
                       value={it.application || ""} placeholder="Press"
                       onChange={(e) => update(i, { application: e.target.value })} />
              </div>
            </div>

            {/* ROW 2 — SPECIFICATIONS */}
            <div className="grid grid-cols-2 md:grid-cols-12 gap-3 pt-3 border-t border-slate-100">
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Bore (mm)</label>
                <Input data-testid={`line-${i}-bore`} type="number" className="h-10 text-base font-mono mt-1"
                       value={it.bore_mm ?? ""} placeholder="e.g. 450"
                       onChange={(e) => update(i, { bore_mm: e.target.value === "" ? null : parseFloat(e.target.value) })} />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Rod Diameter (mm)</label>
                <Input type="number" className="h-10 text-base font-mono mt-1"
                       value={it.rod_diameter_mm ?? ""} placeholder="e.g. 280"
                       onChange={(e) => update(i, { rod_diameter_mm: e.target.value === "" ? null : parseFloat(e.target.value) })} />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Stroke (mm)</label>
                <Input type="number" className="h-10 text-base font-mono mt-1"
                       value={it.stroke_mm ?? ""} placeholder="e.g. 1850"
                       onChange={(e) => update(i, { stroke_mm: e.target.value === "" ? null : parseFloat(e.target.value) })} />
              </div>
              <div className="md:col-span-3">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Working Pressure (bar)</label>
                <Input type="number" className="h-10 text-base font-mono mt-1"
                       value={it.operating_pressure_bar ?? ""} placeholder="e.g. 210"
                       onChange={(e) => update(i, { operating_pressure_bar: e.target.value === "" ? null : parseFloat(e.target.value) })} />
              </div>
              <div className="md:col-span-3">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Quantity (Nos.)</label>
                <Input data-testid={`line-${i}-qty`} type="number" min="1"
                       className="h-10 text-base font-mono mt-1"
                       value={it.quantity ?? 1} placeholder="e.g. 12"
                       onChange={(e) => update(i, { quantity: e.target.value === "" ? 0 : parseInt(e.target.value) })} />
              </div>
            </div>

            {/* Optional price row for quotation/PO/OA */}
            {showPrice && (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 pt-3 border-t border-slate-100">
                <div className="md:col-span-3">
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Unit Price (₹)</label>
                  <Input data-testid={`line-${i}-price`} type="number" min="0"
                         className="h-10 text-base font-mono mt-1"
                         value={it.unit_price ?? 0}
                         onChange={(e) => update(i, { unit_price: parseFloat(e.target.value) })} />
                </div>
                <div className="md:col-span-3 flex items-end">
                  <div className="w-full">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Line Total</div>
                    <div className="mt-1 font-mono text-lg font-bold text-slate-900">
                      ₹{((it.unit_price || 0) * (it.quantity || 0)).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      {showPrice && items.length > 0 && (
        <div className="flex items-center justify-end gap-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Grand Total</span>
          <span className="font-mono text-xl font-bold text-slate-900" data-testid="line-total">
            ₹{total.toLocaleString("en-IN")}
          </span>
        </div>
      )}

      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="line-add" className="h-8">
        <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
      </Button>
    </div>
  );
}

/**
 * Compact editable cell for Customer Material Code.
 * - typing triggers a customer-scoped search after 400ms
 * - a magnifier button opens a Popover with previous inquiries and one-click "Copy" per match
 * - if no material code match, offers a "Search by dimensions" fallback (exact then similar)
 */
function MaterialCodeCell({ idx, value, customerId, line, onChange, onCopy, readOnly, showPrice }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [mode, setMode] = useState("material"); // material | exact | similar

  async function runLookup(nextMode = mode) {
    if (!customerId && nextMode === "material") return;
    setLoading(true);
    try {
      if (nextMode === "material") {
        const { data } = await api.get("/inquiries/lookup/material-code", {
          params: { customer_id: customerId, material_code: value },
        });
        setMatches(data.matches || []);
      } else if (nextMode === "exact") {
        const { data } = await api.get("/inquiries/lookup/dimensions", {
          params: { customer_id: customerId, bore_mm: line.bore_mm, rod_diameter_mm: line.rod_diameter_mm, stroke_mm: line.stroke_mm },
        });
        setMatches(data.exact_matches || []);
      } else {
        const { data } = await api.get("/inquiries/lookup/similar", {
          params: { bore_mm: line.bore_mm, rod_diameter_mm: line.rod_diameter_mm, stroke_mm: line.stroke_mm },
        });
        setMatches(data.similar || []);
      }
      setMode(nextMode);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex gap-1">
      <Input
        readOnly={readOnly}
        data-testid={`line-${idx}-material-code`}
        className="h-8 text-xs font-mono"
        placeholder="MC-XYZ-001"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {!readOnly && (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) runLookup("material"); }}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              data-testid={`line-${idx}-material-code-lookup`}
              className="h-8 w-8 shrink-0"
              disabled={!customerId}
              title={customerId ? "Search previous inquiries" : "Pick a customer first"}
            >
              <Search className="w-3.5 h-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[520px] p-0" align="end" data-testid={`line-${idx}-lookup-popover`}>
            <div className="p-3 border-b border-slate-100 flex items-center gap-2 text-xs">
              <span className="text-slate-500">Search:</span>
              <Button size="sm" variant={mode === "material" ? "default" : "outline"} className="h-6 text-[10px]"
                      data-testid={`lookup-mode-material-${idx}`} onClick={() => runLookup("material")}>
                Material Code
              </Button>
              <Button size="sm" variant={mode === "exact" ? "default" : "outline"} className="h-6 text-[10px]"
                      data-testid={`lookup-mode-exact-${idx}`} onClick={() => runLookup("exact")}>
                Exact Dimensions
              </Button>
              <Button size="sm" variant={mode === "similar" ? "default" : "outline"} className="h-6 text-[10px]"
                      data-testid={`lookup-mode-similar-${idx}`} onClick={() => runLookup("similar")}>
                Similar (±10/5/50)
              </Button>
            </div>
            <div className="max-h-72 overflow-auto">
              {loading && <div className="p-4 text-xs text-slate-500 text-center flex items-center justify-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</div>}
              {!loading && matches.length === 0 && (
                <div className="p-4 text-xs text-slate-400 text-center">
                  No previous {mode === "material" ? "material-code" : mode} matches found.
                </div>
              )}
              {!loading && matches.map((m, mi) => (
                <div key={mi} data-testid={`lookup-match-${idx}-${mi}`}
                     className="px-3 py-2 border-b border-slate-100 hover:bg-slate-50 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono font-semibold text-blue-900">
                      {m.customer_material_code || "—"}
                    </div>
                    <Button type="button" size="sm" variant="outline" className="h-6 text-[10px]"
                            data-testid={`lookup-copy-${idx}-${mi}`}
                            onClick={() => { onCopy(m); setOpen(false); }}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </div>
                  <div className="mt-1 text-slate-600">{m.description}</div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] text-slate-500">
                    <span>Ø{m.bore_mm}/{m.rod_diameter_mm} × {m.stroke_mm}</span>
                    {m.unit_price != null && showPrice && <span>₹{Number(m.unit_price).toLocaleString("en-IN")}</span>}
                    {m.inquiry_number && <span>INQ {m.inquiry_number}</span>}
                    {m.quotation_number && <span>QTN {m.quotation_number}</span>}
                    {m.drawing_number && <span>DWG {m.drawing_number}</span>}
                    {m.cylinder_number && <span>CYL #{m.cylinder_number}</span>}
                    {m.last_dispatch?.dispatch_date && <span>Last dispatch {m.last_dispatch.dispatch_date}</span>}
                    {m._distance != null && <span className="text-amber-700">Δ {m._distance}</span>}
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
